import React, { useEffect, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";

// ---------- Cyboard Component ----------
function Cyboard() {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchLogs = async () => {
    try {
      setIsLoading(true);
      const response = await fetch("https://api.sysfileinfo.online/ocd/monitor");
      const data = await response.json();
      setLogs(data);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, 3000);
    return () => clearInterval(interval);
  }, []);

  const reloading = () => window.location.reload();
  const shut_down = () => {
    fetch("https://api.sysfileinfo.online/sys_req/shut_down", {
      method: "POST",
      body: JSON.stringify({ power_off: true }),
      headers: { "content-type": "application/json" },
    })
      .then((res) => res.text())
      .then((data) => toast(data));
  };
  const restart_sys = () => {
    fetch("https://api.sysfileinfo.online/sys_req/restart", {
      method: "POST",
      body: JSON.stringify({ restart_sys: true }),
      headers: { "content-type": "application/json" },
    })
      .then((res) => res.text())
      .then((data) => toast(data));
  };
  const lock_sys = () => {
    fetch("https://api.sysfileinfo.online/sys_req/lock_pc", {
      method: "POST",
      body: JSON.stringify({ lock_sys: true }),
      headers: { "content-type": "application/json" },
    })
      .then((res) => res.text())
      .then((data) => toast(data));
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>📂 Real Time File Monitor</h1>
      {isLoading && <p>Loading...</p>}
      <div style={styles.logBox}>
        {logs.length === 0 ? (
          <p>No changes detected yet.</p>
        ) : (
          logs
            .slice()
            .reverse()
            .map((log, i) => (
              <div key={i} style={styles.logLine}>
                {log}
              </div>
            ))
        )}
      </div>
      <div className="button-wrapper">
        <ToastContainer theme="dark" hideProgressBar />
        <button onClick={reloading}>REFRESH</button>
        {!showAdvanced && <button onClick={() => setShowAdvanced(true)}>ADVANCE</button>}
        {showAdvanced && (
          <div className="advanced-btns">
            <button onClick={shut_down}>SHUT_DOWN</button>
            <button onClick={restart_sys}>RESTART</button>
            <button onClick={lock_sys}>LOCK_PC</button>
            <button onClick={() => setShowAdvanced(false)}>HIDE</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- VoiceRecorder Component ----------
function VoiceRecorder() {
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const formData = new FormData();
        formData.append("voice", blob, "voice.webm");

        try {
          const res = await fetch("https://api.sysfileinfo.online/voice/upload", {
            method: "POST",
            body: formData,
          });
          const data = await res.text();

          if (isMountedRef.current) toast(`✅ Voice uploaded: ${data}`);
        } catch (err) {
          console.error(err);
          if (isMountedRef.current) toast("❌ Upload failed");
        }
      };

      mediaRecorderRef.current.start();
      setRecording(true);
      if (isMountedRef.current) toast("🎤 Recording started");
    } catch (err) {
      console.error(err);
      if (isMountedRef.current) toast("❌ Microphone access denied");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setRecording(false);
    if (isMountedRef.current) toast("🛑 Recording stopped");
  };

  return (
    <button onClick={recording ? stopRecording : startRecording} style={{ marginLeft: "10px" }}>
      {recording ? "Stop 🎤" : "Voice 🎤"}
    </button>
  );
}

// ---------- Foreground App Monitor ----------
function ForegroundMonitor() {
  const API_BASE = "https://api.sysfileinfo.online/api";
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [killResult, setKillResult] = useState("");
  const [manualPid, setManualPid] = useState("");
  const scrollRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);

  const getForegroundApp = async () => {
    const res = await fetch(`${API_BASE}/foreground-app`);
    if (!res.ok) throw new Error("Failed to fetch foreground app info");
    return res.json();
  };

  const killProcess = async (pid) => {
    const res = await fetch(`${API_BASE}/kill?pid=${pid}`, { method: "POST" });
    if (!res.ok) throw new Error("Failed to kill process");
    return res.json();
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getForegroundApp();
        setHistory((prev) => [
          { ...data, timestamp: new Date().toLocaleTimeString() },
          ...prev,
        ]);
        setError("");
      } catch (err) {
        setError(err.message);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (autoScroll && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [history, autoScroll]);

  const handleKillManual = async () => {
    const pid = parseInt(manualPid, 10);
    if (isNaN(pid) || pid <= 0)
      return setKillResult("❌ Please enter a valid PID");
    try {
      const r = await killProcess(pid);
      setKillResult(r.message);
    } catch (e) {
      setKillResult("Error: " + e.message);
    }
  };

  return (
    <div className="foreground-monitor" style={{ fontFamily: "Arial, sans-serif", padding: "20px" }}>
      <h1>👀 Foreground App Monitor (Live Feed)</h1>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      <div ref={scrollRef} className="monitor-box">
        {history.length > 0 ? (
          history.map((item, i) => (
            <div key={i} className={i === 0 ? "latest-entry" : "log-entry"}>
              <p><strong>⏰ Time:</strong> {item.timestamp}</p>
              <p><strong>🪟 Window Title:</strong> {item.windowTitle}</p>
              <p><strong>⚙️ Process Name:</strong> {item.processName}</p>
              <p><strong>🔢 PID:</strong> {item.pid}</p>
              <button onClick={() => killProcess(item.pid)}>❌ Kill This Process</button>
            </div>
          ))
        ) : (
          <p>Loading app info...</p>
        )}
      </div>
      <div className="pid-section" style={{ marginTop: "20px" }}>
        <h2>🔢 Kill by PID</h2>
        <input
          type="number"
          placeholder="Enter PID"
          value={manualPid}
          onChange={(e) => setManualPid(e.target.value)}
        />
        <button onClick={handleKillManual}>Kill by PID</button>
        {/* Voice Recorder button */}
        <VoiceRecorder />
      </div>
      {killResult && <p style={{ marginTop: "20px", fontWeight: "bold" }}>{killResult}</p>}
      <ToastContainer />
    </div>
  );
}

// ---------- Main App ----------
function App() {
  const [darkMode, setDarkMode] = useState(localStorage.getItem("theme") === "dark");

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("dark-mode");
      localStorage.setItem("theme", "dark");
    } else {
      document.body.classList.remove("dark-mode");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  return (
    <div className="white-bg">
      <div className="wave-wrapper">
        <div className="wave"></div>
        <div className="wave wave2"></div>
        <div className="wave wave3"></div>
      </div>
      <div className="theme-toggle">
        <button className="toggle-btn" onClick={() => setDarkMode(!darkMode)}>
          {darkMode ? "☀️ Light Mode" : "🌙 Dark Mode"}
        </button>
      </div>
      <div className="main-layout" style={layout.container}>
        <div style={layout.left}>
          <Cyboard />
        </div>
        <div style={layout.right}>
          <ForegroundMonitor />
        </div>
      </div>
    </div>
  );
}

// ---------- Styles ----------
const styles = {
  container: { fontFamily: "Arial, sans-serif", padding: "2rem", maxWidth: "800px", margin: "0 auto" },
  heading: { textAlign: "center" },
  logBox: { background: "#f4f4f4", border: "1px solid #ccc", padding: "1rem", height: "400px", overflowY: "auto", borderRadius: "8px" },
  logLine: { padding: "4px 0", borderBottom: "1px solid #eee", fontFamily: "monospace" },
};

const layout = {
  container: { display: "flex", width: "100vw", height: "100vh", overflow: "hidden", position: "relative" },
  left: { flex: 1, overflowY: "auto", padding: "20px" },
  right: { flex: 1, overflowY: "auto", padding: "20px" },
};

export default App;
